import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { PersonIdentityDialog } from "../components/shopfloor/PersonIdentityDialog";

/** Identificazione della persona di officina: il tablet è condiviso, quindi prima di scrivere si chiede «Chi sei?».
 *
 * - chi ha un account dell'app è già identificato (nessun PIN);
 * - sugli account condivisi si sceglie il proprio nome fra le persone che l'amministratore ha associato a quell'account
 *   e si digita il PIN personale di 4 cifre;
 * - l'identificazione vale per la singola scansione (3 minuti), poi viene richiesta di nuovo;
 * - se l'azienda non ha ancora persone in anagrafica, tutto funziona come prima (vale il nome dell'account).
 */
const REUSE_MS = 3 * 60 * 1000;
const EMPTY = { token: "", name: "" };

const ShopfloorIdentityContext = createContext(null);

export function ShopfloorIdentityProvider({ children }) {
  const { session } = useAuth();
  const [request, setRequest] = useState(null);
  const identity = useRef({ ...EMPTY, expires: 0 });

  const remember = useCallback((token, name) => {
    identity.current = { token, name, expires: Date.now() + REUSE_MS };
    return { token, name };
  }, []);

  const resetPerson = useCallback(() => { identity.current = { ...EMPTY, expires: 0 }; }, []);

  // cambio account (logout compreso): l'identificazione precedente decade subito
  useEffect(() => { resetPerson(); }, [session?.user?.id, resetPerson]);

  const requirePerson = useCallback(async () => {
    if (identity.current.token && identity.current.expires > Date.now()) {
      return { token: identity.current.token, name: identity.current.name };
    }
    let registry = null;
    try {
      registry = (await api.shopfloorPeople()).data;
    } catch {
      return EMPTY;   // anagrafica non raggiungibile: vale il nome dell'account collegato
    }
    const people = registry.people || [];
    // il PIN si chiede solo sulle postazioni condivise di officina: chi entra col proprio account è già identificato
    if (!registry.requires_pin) return remember("", registry.current_person || "");
    if (!people.length) {
      toast.error("Nessuna persona è collegata a questa postazione: chiedi all'amministratore di associarti a questo account");
      return null;
    }
    return new Promise((resolve) => setRequest({ resolve, people, withoutPin: registry.without_pin || [] }));
  }, [remember]);

  const close = (result) => {
    request?.resolve(result);
    setRequest(null);
  };

  return (
    <ShopfloorIdentityContext.Provider value={{ requirePerson, resetPerson, personName: identity.current.name }}>
      {children}
      {request && (
        <PersonIdentityDialog
          people={request.people}
          withoutPin={request.withoutPin}
          onConfirm={(data) => close(remember(data.token, data.person.name))}
          onCancel={() => close(null)}
        />
      )}
    </ShopfloorIdentityContext.Provider>
  );
}

export function useShopfloorIdentity() {
  return useContext(ShopfloorIdentityContext) || { requirePerson: async () => EMPTY, resetPerson: () => {}, personName: "" };
}
